<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SharePointConnectionHealth } from '@/services/api_client';

import SharePointPasswordField from './SharePointPasswordField.vue';

defineProps<{
    areInvalidCredentials: boolean;
    areSharepointCredentialsSet: boolean;
    connectionHealth: SharePointConnectionHealth | null;
    isHealthLoading: boolean;
    isSubmitDisabled: boolean;
}>();

const emit = defineEmits<{
    submit: [];
}>();

const microsoftAppId = defineModel<string>('microsoftAppId', {
    required: true
});
const azureTenantId = defineModel<string>('azureTenantId', { required: true });
const microsoftAppPassword = defineModel<string>('microsoftAppPassword', {
    required: true
});
</script>

<template>
    <form class="space-y-4" @submit.prevent="emit('submit')">
        <div class="space-y-2">
            <Label for="sharepoint-microsoft-app-id"> Microsoft App ID </Label>
            <Input
                id="sharepoint-microsoft-app-id"
                v-model="microsoftAppId"
                autocomplete="off"
                required
            />
        </div>

        <div class="space-y-2">
            <Label for="sharepoint-azure-tenant-id"> Azure Tenant ID </Label>
            <Input
                id="sharepoint-azure-tenant-id"
                v-model="azureTenantId"
                autocomplete="off"
                required
            />
        </div>

        <div class="space-y-2">
            <Label for="sharepoint-microsoft-app-password">
                Microsoft App Password
            </Label>
            <SharePointPasswordField
                id="sharepoint-microsoft-app-password"
                v-model="microsoftAppPassword"
            />
        </div>

        <p v-if="areInvalidCredentials" class="text-sm text-destructive">
            Invalid credentials. Please verify your SharePoint app details.
        </p>
        <p
            v-else-if="areSharepointCredentialsSet && isHealthLoading"
            class="text-sm text-muted-foreground"
        >
            Checking the Microsoft 365 connection…
        </p>
        <div
            v-else-if="
                areSharepointCredentialsSet &&
                connectionHealth &&
                connectionHealth.status !== 'not_configured'
            "
            class="space-y-1 text-sm"
        >
            <p
                :class="
                    connectionHealth.status === 'healthy'
                        ? 'text-green100'
                        : connectionHealth.status === 'unavailable'
                          ? 'text-yellow100 dark:text-foreground'
                          : 'text-destructive'
                "
            >
                <!-- The shared detail sends members to an administrator; here the reader is one. -->
                {{
                    connectionHealth.status === 'invalid_secret'
                        ? 'The client secret is invalid or expired.'
                        : connectionHealth.detail
                }}
            </p>
            <p
                v-if="connectionHealth.status === 'invalid_secret'"
                class="text-destructive"
            >
                SharePoint and Azure AD groups are unavailable. Microsoft 365
                connectors still work.
            </p>
            <p
                v-else-if="connectionHealth.status === 'missing_permission'"
                class="text-destructive"
            >
                SharePoint is unavailable. Microsoft 365 connectors still work.
            </p>
        </div>
        <div class="flex justify-end">
            <Button type="submit" :disabled="isSubmitDisabled">
                {{
                    areSharepointCredentialsSet
                        ? 'Update connection'
                        : 'Connect'
                }}
            </Button>
        </div>
    </form>
</template>
